import { IManagedUserRecentSummary, IManagerProduct } from '@/types';
import { TManagerDashboardFilter } from '../types';
import { useCallback } from 'react';

/** Props for the useManagerDashboardProductFiltering hook */
interface UseManagerDashboardProductFilteringProps {
  /** The currently active filter applied to the dashboard */
  activeFilter: TManagerDashboardFilter;
  /** The list of all managed users */
  managedUsers: IManagedUserRecentSummary[];
  /** The currently active certification product for the manager */
  managerActiveProduct: IManagerProduct | null;
  /** The list of users who are certified for the active certification */
  usersCertified: IManagedUserRecentSummary[];
  /** The list of users who are either failing or have not attempted the certification */
  usersNeedingAttention: IManagedUserRecentSummary[];
  /** The list of users who have passed the practice certification */
  usersPassedPractice: IManagedUserRecentSummary[];
  /** Function to update the active filter state */
  setActiveFilter: (filter: TManagerDashboardFilter) => void;
  /** Function to update the filtered users state */
  setFilteredUsers: (users: IManagedUserRecentSummary[]) => void;
}

/**
 * Hook to manage product filtering logic for the Manager Dashboard.
 * Handles filter selection and updates filtered users accordingly.
 */
export const useManagerDashboardProductFiltering = ({
  activeFilter,
  managedUsers,
  managerActiveProduct,
  usersCertified,
  usersNeedingAttention,
  usersPassedPractice,
  setActiveFilter,
  setFilteredUsers,
}: UseManagerDashboardProductFilteringProps) => {
  const handleFilterSelect = useCallback(
    (value: TManagerDashboardFilter): void => {
      switch (value) {
        case 'certified':
          if (activeFilter === 'certified') {
            setActiveFilter(null);
            setFilteredUsers(managedUsers);
            return;
          }

          if (!managerActiveProduct) return;

          setActiveFilter('certified');
          setFilteredUsers(usersCertified);
          break;

        case 'practicePassed':
          if (activeFilter === 'practicePassed') {
            setActiveFilter(null);
            setFilteredUsers(managedUsers);
            return;
          }

          if (!managerActiveProduct) return;

          setActiveFilter('practicePassed');
          setFilteredUsers(usersPassedPractice);
          break;

        case 'needsAttention':
          if (activeFilter === 'needsAttention') {
            setActiveFilter(null);
            setFilteredUsers(managedUsers);
            return;
          }

          if (!managerActiveProduct) return;

          setActiveFilter('needsAttention');
          setFilteredUsers(usersNeedingAttention);
          break;

        default:
          setActiveFilter(null);
          setFilteredUsers(managedUsers);
      }
    },
    [
      activeFilter,
      managedUsers,
      managerActiveProduct,
      usersCertified,
      usersPassedPractice,
      usersNeedingAttention,
      setActiveFilter,
      setFilteredUsers,
    ],
  );

  return { handleFilterSelect };
};
